import { HTTP } from "@/core/http";
import type {
  RiskApiResponse,
  RiskHistoryApiResponse,
  RiskMetadataApiResponse,
  RiskWriteItem,
} from "@/shared/api";
import { dateToLocalYYYYMMDD } from "@/shared/utils/dateFormat";
import { downloadFromResponse } from "@/shared/utils/downloadBlob";
import type {
  Risk,
  RiskFilters,
  GenerateRisksParams,
  GeneratedRisk,
} from "../types";

// Re-export Risk type for backward compatibility
export type { Risk };

export interface RiskReportRequest {
  projectId: number;
  format?: "csv" | "pdf" | "excel";
}

export class RiskService {
  constructor(private readonly httpClient: HTTP) {}

  /**
   * Calculate risk severity based on probability and impact
   */
  private calculateSeverity(
    probability?: number | null,
    impact?: number | null
  ): "high" | "medium" | "low" {
    if (!probability || !impact) return "low";
    const exposure = probability * impact;
    if (exposure >= 15) return "high";
    if (exposure >= 8) return "medium";
    return "low";
  }

  /**
   * Add severity to risk if missing (client-only field)
   */
  private addSeverityIfMissing(risk: RiskApiResponse): Risk {
    if (!risk.severity && risk.riskProbability && risk.riskImpact) {
      risk.severity = this.calculateSeverity(
        risk.riskProbability,
        risk.riskImpact
      );
    }
    return risk;
  }

  /**
   * GET /pms/risks/metadata — realized-flag key is snake_case `risk_realized_flag`.
   */
  async getRisksMetadata(): Promise<RiskMetadataApiResponse> {
    return this.httpClient.get<RiskMetadataApiResponse>("/pms/risks/metadata");
  }

  /**
   * Get risks with optional filters (projectId, status[], source[])
   */
  async getRisks(filters?: RiskFilters): Promise<Risk[]> {
    const params: Record<string, string | number | string[]> = {};

    if (filters?.status?.length) {
      params.status = filters.status;
    }
    if (filters?.source?.length) {
      params.source = filters.source;
    }

    if (filters?.projectId) {
      const url = `/pms/risks/project/${filters.projectId}`;
      const risks = await this.httpClient.get<RiskApiResponse[]>(url, {
        params,
      });
      return risks.map((risk) => this.addSeverityIfMissing(risk));
    }

    const risks = await this.httpClient.get<RiskApiResponse[]>("/pms/risks", {
      params,
    });
    return risks.map((risk) => this.addSeverityIfMissing(risk));
  }

  /**
   * GET /pms/risks/{riskId}
   */
  async getRiskById(id: number): Promise<Risk> {
    const risk = await this.httpClient.get<RiskApiResponse>(
      `/pms/risks/${id}`,
    );
    return this.addSeverityIfMissing(risk);
  }

  /**
   * POST /pms/risks — array body; enums as plain strings.
   */
  async createRisks(
    data: Array<Partial<RiskWriteItem>>,
  ): Promise<Risk[]> {
    const risks = await this.httpClient.post<
      RiskApiResponse[],
      Array<Partial<RiskWriteItem>>
    >("/pms/risks", {
      body: data,
    });
    return risks.map((risk) => this.addSeverityIfMissing(risk));
  }

  /**
   * PUT /pms/risks/{riskId} — enums as plain strings.
   */
  async updateRisk(
    riskId: number,
    data: Partial<RiskWriteItem> & { action?: string },
  ): Promise<Risk> {
    const risk = await this.httpClient.put<
      RiskApiResponse,
      Partial<RiskWriteItem> & { action?: string }
    >(`/pms/risks/${riskId}`, {
      body: data,
    });
    return this.addSeverityIfMissing(risk);
  }

  /**
   * DELETE /pms/risks/{riskId}
   */
  async deleteRisk(id: number): Promise<void> {
    return this.httpClient.delete<void>(`/pms/risks/${id}`);
  }

  /**
   * Download risk report
   * @param autoDownload - If true, automatically triggers download. If false, returns blob for custom handling.
   */
  async downloadRiskReport(
    projectId: number,
    format: "csv" | "pdf" | "excel" = "csv",
    autoDownload: boolean = true,
  ): Promise<Blob> {
    const response = await this.httpClient.get<Response>(
      `/pms/risks/project/${projectId}/report`,
      {
        params: { format },
        returnRawResponse: true,
      },
    );

    return downloadFromResponse(
      response,
      `risks_report_${projectId}_${dateToLocalYYYYMMDD(new Date())}.${format}`,
      autoDownload,
    );
  }

  /**
   * POST /pms/risks/generate/{projectId} — AI Brain reads the project's RFP
   * Analysis from PMS and saves the generated risks as AI / Draft. The response
   * is the AI-native risk list; refetch the project risks to get saved rows.
   */
  async generateRisks(params: GenerateRisksParams): Promise<GeneratedRisk[]> {
    const raw = await this.httpClient.post<unknown>(
      `/pms/risks/generate/${params.projectId}`,
    );
    if (!Array.isArray(raw)) {
      throw new Error("Risk generation did not complete. Please try again.");
    }
    return raw as GeneratedRisk[];
  }

  /**
   * GET /pms/risks/{riskId}/history
   * Backend returns history sorted by timestamp (latest first).
   * History enums are plain strings.
   */
  async getRiskHistory(riskId: number): Promise<RiskHistoryApiResponse[]> {
    return this.httpClient.get<RiskHistoryApiResponse[]>(
      `/pms/risks/${riskId}/history`,
    );
  }
}
